import { NcpError, ncp } from "@ncp/page-sdk";
import type { ClientToolCall, OpenPage } from "@ncp/assistant-sdk";
import { toast } from "@ncp/ui";
import type { PageSessionInfo } from "./bridge";

/** The workspace's handle on an open page: what the copilot sends with a message, and how answers reach back into the frame. */
export interface PageLink {
  pageKey: string;
  space: string | null;
  name: string;
  nameAr: string | null;
  agents: PageSessionInfo["agents"];
  /** The page for a copilot message: a fresh session token and what the page last reported it shows. */
  open(): Promise<OpenPage>;
  /** The copilot's agent called one of the page's client tools; the page's answer goes to the waiting turn. */
  runTool(call: ClientToolCall): void;
  /** A capability call in the page's session (buttons and forms in the copilot's answer blocks). */
  call(capability: string, input: unknown, signal?: AbortSignal): Promise<unknown>;
  /** Runs one of the page's client tools for the host and returns its answer. */
  invokeTool(name: string, args: unknown): Promise<unknown>;
  /** The names of the client tools the page declares (page.json `clientTools`), so the host shows only actions the page can run. */
  clientTools?: string[];
  /** Puts the page in presentation or immersive mode, or back (shell-ia §15.1 "Present"). */
  setMode?(mode: "normal" | "immersive" | "presentation"): void;
}

/** In a matter's address, the parameters of the page on the open tab carry this prefix (`?tab=page:x&p.view=requests`). */
export const PAGE_PARAM = "p.";

/** The window event a page's `ncp.ui.open({ useCase })` raises; the workspace answers it with its start dialog. */
export const START_USE_CASE = "ncp:start-use-case";
export interface StartUseCase { useCase: string; params: Record<string, string>; title?: string; summary?: string }

/** Where a page (or a block in an answer) asked to go: another page, a matter, a use case to start, a workspace path or an outside link. */
export function openTarget(navigate: (to: string) => void, target: Record<string, unknown>): void {
  if (typeof target.page === "string") {
    const params = target.params ? `?${new URLSearchParams(target.params as Record<string, string>)}` : "";
    const path = typeof target.path === "string" && target.path ? `/${target.path.replace(/^\/+/, "")}` : "";
    navigate(`/p/${encodeURIComponent(target.page)}${path}${params}`);
  } else if (typeof target.matter === "string") {
    const query = new URLSearchParams();
    if (typeof target.tab === "string") query.set("tab", target.tab);
    if (target.params && typeof target.params === "object") for (const [k, v] of Object.entries(target.params)) query.set(`${PAGE_PARAM}${k}`, String(v));
    const q = query.toString();
    navigate(`/matters/${encodeURIComponent(target.matter)}${q ? `?${q}` : ""}`);
  } else if (typeof target.useCase === "string") {
    window.dispatchEvent(new CustomEvent<StartUseCase>(START_USE_CASE, { detail: {
      useCase: target.useCase,
      params: target.params && typeof target.params === "object" ? Object.fromEntries(Object.entries(target.params).map(([k, v]) => [k, String(v)])) : {},
      title: typeof target.title === "string" ? target.title : undefined,
      summary: typeof target.summary === "string" ? target.summary : undefined,
    } }));
  } else if (typeof target.url === "string") {
    const url = target.url;
    if (url.startsWith("/") && !url.startsWith("//")) navigate(url);
    else if (/^https?:\/\//i.test(url)) window.open(url, "_blank", "noopener,noreferrer");
  }
}

let active: PageLink | null = null;
let host = { locale: "en", navigate: (_to: string) => undefined as void };

/** The capability transport of a native ECONIC screen (shell-ia §19.1/§19.4): calls as the viewer, under the surface in view. */
export interface SurfaceTransport {
  call: (capability: string, input: unknown, signal?: AbortSignal) => Promise<unknown>;
}
let surface: SurfaceTransport | null = null;

/** The page in view, whose session the blocks drawn in the workspace act in. */
export function setActivePageLink(link: PageLink | null): void {
  active = link;
}

/**
 * The fallback for page-kit components drawn on native ECONIC screens (a KpiCard with a query, Blocks with actions) when no page is in
 * view: their `ncp.call` goes to the surface's transport (`apps/assistant/src/econic/data/surfaceTransport.ts`, POST /api/econic/rpc as
 * the viewer). A page in view always wins. Null removes it.
 */
export function setSurfaceTransport(t: SurfaceTransport | null): void {
  surface = t;
}

const noPage = () => new NcpError("standalone", host.locale === "ar" ? "يلزم فتح الصفحة لاستخدام هذا الإجراء." : "Open the page to use this action.");

let installed = false;

/** Lets page-kit run in the workspace itself (the UI blocks in copilot answers): its calls go to the page in view, its links to the router. */
export function installPageEmbed(locale: string, navigate: (to: string) => void): void {
  host = { locale, navigate };
  if (installed) return;
  installed = true;
  ncp.embed({
    locale: () => host.locale,
    call: (capability, input, signal) =>
      active ? active.call(capability, input, signal) : surface ? surface.call(capability, input, signal) : Promise.reject(noPage()),
    invokeTool: (name, args) => (active ? active.invokeTool(name, args) : Promise.reject(noPage())),
    host: async (op, args) => {
      const a = (args ?? {}) as Record<string, unknown>;
      switch (op) {
        case "toast": {
          const show = a.tone === "success" ? toast.success : a.tone === "danger" ? toast.error : a.tone === "warning" ? toast.warning : toast;
          show(String(a.message ?? "").slice(0, 300), a.description ? { description: String(a.description).slice(0, 600) } : undefined);
          return null;
        }
        case "open":
        case "navigate":
          openTarget(host.navigate, a);
          return null;
        default:
          throw new NcpError("standalone", `"${op}" runs inside a custom page.`);
      }
    },
  });
}
